import { useCallback, useEffect, useRef, useState } from 'react'
import { useBlocker } from 'react-router-dom'

import { Button } from '../components/Button.js'
import { Sheet } from '../components/Sheet.js'
import { useDeclareUnsaved } from '../pwa/UnsavedWork.js'
import styles from './DiscardGuard.module.css'

export interface DiscardGuard {
  /** Whether this editor is holding anything. Drives the visible indicator. */
  readonly dirty: boolean
  /** Whether a navigation is currently being held up by the guard. */
  readonly blocked: boolean
  /** Let the held-up navigation through, losing what was typed. */
  readonly discard: () => void
  /** Cancel it and stay. */
  readonly keepEditing: () => void
  /**
   * "I am about to leave on purpose."
   *
   * Called immediately before navigating after a successful save. Without it
   * the guard blocks the app's own post-save navigation: `setDirty(false)` and
   * `navigate()` run in the same tick, and the blocker is still armed when the
   * navigation starts because the re-render has not happened yet.
   */
  readonly allowLeaving: () => void
}

/**
 * One fact, three consequences (T-808).
 *
 * "Is the user holding something?" decides the visible indicator, the discard
 * confirmation, **and** whether the service worker's update prompt is deferred.
 * Answering it in three places means the day they disagree is the day someone
 * loses a half-filled editor to a background reload, which is exactly what
 * T-204's rule exists to prevent. So it is one argument, here.
 *
 * Built on the router's own `useBlocker`, which covers the back button and the
 * back gesture as well as taps — the requirement most likely to be skipped,
 * because it is the one that cannot be reached by clicking around the app.
 *
 * **Not for the log flow.** That screen persists its draft as you go and
 * restores it on return, so leaving loses nothing and a confirmation would be
 * asking about something that is not at stake. A guard that fires when there is
 * no risk is how people learn to dismiss the one that matters.
 */
export function useDiscardGuard(dirty: boolean): DiscardGuard {
  useDeclareUnsaved(dirty)
  const leaving = useRef(false)

  // Re-arm as soon as there is something to protect again, so one deliberate
  // exit does not disarm the guard for the rest of the screen's life.
  useEffect(() => {
    if (dirty) {
      leaving.current = false
    }
  }, [dirty])

  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }) => {
        /*
         * Only a navigation that actually leaves this screen.
         *
         * A `Sheet` closes by popping its own history entry (T-803), which is
         * a navigation to the *same* path. Blocking that put the router and the
         * browser's history out of step and blanked the app — the guard held a
         * POP the sheet had already committed to. It is also plainly the right
         * rule: this guard exists to stop you leaving an editor, and staying on
         * it is not leaving.
         */
        if (currentLocation.pathname === nextLocation.pathname) {
          return false
        }
        return dirty && !leaving.current
      },
      [dirty],
    ),
  )

  /*
   * The blocker, read at call time rather than closed over.
   *
   * `proceed` and `reset` are recreated on every render, and a closure over one
   * render's pair is only reliable for a caller that acts before the next
   * render. Everything here used to do exactly that — a click, then straight to
   * `proceed()`. The sheet's Save does not: it re-renders to disable its
   * buttons, awaits a write, and only then decides. Under test that older pair
   * was inert and the sheet sat there with nothing happening; in a browser it
   * still worked, which is the worst of both worlds to rely on.
   *
   * So the pair is read from a ref, which also makes the three callbacks stable
   * identities — nothing downstream re-renders because the guard object changed.
   */
  const current = useRef(blocker)
  current.current = blocker

  return {
    dirty,
    blocked: blocker.state === 'blocked',
    discard: useCallback(() => current.current.proceed?.(), []),
    keepEditing: useCallback(() => current.current.reset?.(), []),
    allowLeaving: useCallback(() => {
      leaving.current = true
    }, []),
  }
}

interface Props {
  readonly guard: DiscardGuard
  /**
   * What is at stake, in the editor's own words — "this track", "your changes
   * to Morning". The confirmation names it rather than asking "are you sure",
   * which tells the user nothing they did not already know.
   */
  readonly what: string
  /**
   * Save what is held, and report whether it worked.
   *
   * Two rules, and both are the difference between this button helping and
   * doing harm:
   *
   *   - **It must not navigate.** The guard is already holding a navigation the
   *     user chose, and continuing *that* is the whole point — sending them to
   *     the editor's usual post-save destination instead would answer a
   *     question they did not ask.
   *   - **`false` must leave them on the editor.** A Save that fails validation
   *     and leaves anyway loses the work and hides the reason, which is worse
   *     than having no Save at all.
   *
   * Optional, so an editor with nothing sensible to save from here simply does
   * not offer it.
   */
  readonly onSave?: () => Promise<boolean>
}

/** The indicator and the confirmation, both driven by the same guard. */
export function UnsavedWorkGuard({ guard, what, onSave }: Props) {
  const [saving, setSaving] = useState(false)

  async function saveAndLeave() {
    if (onSave === undefined || saving) {
      return
    }
    setSaving(true)
    const saved = await onSave()
    setSaving(false)
    if (saved) {
      // The navigation the user chose before being interrupted, not one of this
      // component's choosing.
      guard.allowLeaving()
      guard.discard()
      return
    }
    // Stay, with the errors the save just set now visible on the editor behind
    // the sheet. Closing it is what makes them readable.
    guard.keepEditing()
  }

  return (
    <>
      {guard.dirty && (
        <p className={styles['indicator']} role="status">
          <span className={styles['dot']} aria-hidden="true" />
          Unsaved changes
        </p>
      )}

      <Sheet
        open={guard.blocked}
        title={
          onSave === undefined ? 'Discard your changes?' : 'You have unsaved changes'
        }
        // No corner control. It would be a further way out of the sheet and the
        // ambiguous one — every answer here is spelled out below instead.
        dismissLabel={null}
        onClose={guard.keepEditing}
      >
        <div className={styles['confirm']}>
          <p>
            {what}{' '}
            {onSave === undefined
              ? 'has unsaved changes. Leaving now loses them — nothing that is already saved is affected.'
              : 'has unsaved changes. Nothing that is already saved is affected.'}
          </p>
          {/*
            Stacked, full width, one decision per row.
            
            Side by side, two of these read as a pair and the third as an
            afterthought, and at 375px three labels this long either wrap or
            shrink below the 44px target. Stacking also means a mis-tap hits the
            neighbouring *answer* rather than whichever button the thumb clipped.

            The order is deliberate and is not the reading order of the title:
            the safe answer sits **closest to the thumb** at the bottom of a
            bottom sheet, where a careless tap lands, and the destructive one is
            kept out of that position.
          */}
          <div className={styles['confirmActions']}>
            {onSave !== undefined && (
              <Button
                variant="primary"
                fullWidth
                disabled={saving}
                onClick={() => void saveAndLeave()}
              >
                {saving ? 'Saving…' : 'Save and leave'}
              </Button>
            )}
            <Button variant="danger" fullWidth disabled={saving} onClick={guard.discard}>
              Discard changes
            </Button>
            <Button fullWidth disabled={saving} onClick={guard.keepEditing}>
              Keep editing
            </Button>
          </div>
        </div>
      </Sheet>
    </>
  )
}
